<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { GranularityBuilders } from "@/modules/explorer/components/PeriodFilterSelector/Components/GranularityBuilders";
import type { Granularity } from "@/modules/explorer/components/PeriodFilterSelector/StateMachine";
import FilterValueDTO = App.Data.Explorer.Filter.FilterValueDTO;

const props = defineProps<{ options: Granularity[] }>();
const model = defineModel<FilterValueDTO[]>();
const selected = defineModel<Granularity>("granularity", {
	default: "60min", // TODO: props.options[0]
});

const filter = ref("");

// Get interval value from granularity string
const getIntervalFromGranularity = (granularity: Granularity): number => {
	// Extract the number from the string (e.g., "60min" -> 60)
	const match = granularity.match(/^(\d+)min$/);
	return match ? parseInt(match[1], 10) : 60; // Default to 60 if pattern doesn't match
};

// Generate all possible time slots for the selected granularity
const timeSlots = computed(() => {
	switch (selected.value) {
		case "1min":
		case "5min":
		case "10min":
		case "15min":
		case "30min":
		case "60min":
		case "360min": {
			const interval = getIntervalFromGranularity(selected.value);
			return GranularityBuilders.Minutes(interval, model.value);
		}
		default:
			throw new Error(`Granularity ${selected.value} not implemented`);
	}
});

// Filter time slots based on search text
const filteredTimeSlots = computed(() => {
	if (!timeSlots.value) return [];

	const searchTerm = filter.value.toLowerCase().trim();
	if (!searchTerm) return timeSlots.value;

	return timeSlots.value.filter((slot) =>
		slot.label.toLowerCase().includes(searchTerm)
	);
});

// Check if a time slot is selected by comparing its ID and label with model values
const isTimeSlotSelected = (slot: FilterValueDTO): boolean => {
	return !!model.value?.some((item) => item.id === slot.id);
};

// Toggle selection of a time slot
const toggleTimeSlot = (slot: FilterValueDTO): void => {
	const isSelected = isTimeSlotSelected(slot);

	if (isSelected) {
		// Remove the slot from the model
		model.value = model.value.filter((item) => item.id !== slot.id);
	} else {
		// Add the slot to the model
		model.value.push({ ...slot });
	}
};

// Count of selected time slots
const selectedCount = computed(() => model.value?.length || 0);

// Select all filtered time slots
const selectAll = () => {
	if (!model.value) {
		model.value = [];
	}

	// Add all filtered slots that aren't already selected
	filteredTimeSlots.value.forEach((slot) => {
		if (!isTimeSlotSelected(slot)) {
			model.value.push({ ...slot });
		}
	});
};

// Clear all selections
const selectNone = () => {
	model.value = [];
};

// Computed properties to control button states
const isAllSelected = computed(() => {
	if (filteredTimeSlots.value.length === 0) return false;

	// Check if all filtered slots are in the model
	return filteredTimeSlots.value.every((slot) => isTimeSlotSelected(slot));
});

const isNoneSelected = computed(() => {
	return selectedCount.value === 0;
});

// Watch for changes in selected granularity to clear filter
watch(selected, () => {
	filter.value = "";
});
</script>

<template>
	<div class="time-slot-selector">
		<div class="px-4 pt-4 pb-3">
			<!-- Search filter -->
			<v-text-field
				v-model="filter"
				label="Filter time slots"
				density="compact"
				hide-details
				variant="outlined"
				prepend-inner-icon="mdi-magnify"
				class="mb-2"
				bg-color="surface"
			/>

			<!-- Granularity selector -->
			<div class="mb-3">
				<div
					class="text-caption text-medium-emphasis mb-1 px-1 d-flex align-center"
				>
					<v-icon
						icon="fa-regular fa-clock"
						color="primary"
						size="small"
						class="mr-1"
					/>
					Time Granularity
				</div>
				<v-btn-toggle
					v-model="selected"
					color="primary"
					density="comfortable"
					mandatory
					rounded="lg"
					class="granularity-selector"
					variant="text"
				>
					<v-btn
						v-for="(option, index) in options"
						:key="index"
						:value="option"
						class="flex-grow-1"
					>
						{{ option }}
					</v-btn>
				</v-btn-toggle>
			</div>

			<!-- Select All/None buttons -->
			<div class="d-flex align-center justify-space-between mb-2">
				<div class="text-caption text-medium-emphasis px-1">
					<span v-if="selectedCount > 0">{{ selectedCount }} selected</span>
					<span v-else>No selections</span>
				</div>
				<div class="selection-actions d-flex gap-2">
					<v-btn
						size="small"
						variant="text"
						density="comfortable"
						@click="selectAll"
						:disabled="isAllSelected"
						class="selection-btn"
						color="primary"
					>
						<v-icon icon="mdi-check-all" size="small" class="mr-1" />
						Select All
					</v-btn>
					<v-btn
						size="small"
						variant="text"
						density="comfortable"
						@click="selectNone"
						:disabled="isNoneSelected"
						class="selection-btn"
						color="error"
					>
						<v-icon icon="mdi-close-circle-outline" size="small" class="mr-1" />
						Clear
					</v-btn>
				</div>
			</div>

			<!-- Time slots as chips -->
			<div class="time-slots-container">
				<v-chip
					v-for="(slot, index) in filteredTimeSlots"
					:key="index"
					:class="{
						selected: isTimeSlotSelected(slot),
						'time-slot-chip': true,
					}"
					@click="toggleTimeSlot(slot)"
					variant="outlined"
					size="small"
					:color="isTimeSlotSelected(slot) ? 'primary' : ''"
					class="ma-1"
				>
					<template v-slot:prepend v-if="isTimeSlotSelected(slot)">
						<v-icon icon="mdi-check" size="x-small" class="mr-1" />
					</template>
					{{ slot.label }}
				</v-chip>

				<div
					v-if="filteredTimeSlots.length === 0"
					class="empty-state text-center pa-4 rounded"
					style="grid-column: span 3"
				>
					<v-icon
						icon="mdi-clock-remove-outline"
						color="medium-emphasis"
						size="large"
						class="mb-2"
					/>
					<div class="text-medium-emphasis">No time slots available</div>
				</div>
			</div>
		</div>
	</div>
</template>

<style scoped lang="scss">
.time-slots-container {
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
	gap: 8px;
	border-radius: 8px;
	max-height: 181px;
	overflow-y: auto;
	padding: 8px 4px;

	&::-webkit-scrollbar {
		width: 6px;
	}

	&::-webkit-scrollbar-track {
		background: rgba(var(--v-theme-on-surface), 0.04);
		border-radius: 3px;
	}

	&::-webkit-scrollbar-thumb {
		background: rgba(var(--v-theme-on-surface), 0.12);
		border-radius: 3px;
	}

	&::-webkit-scrollbar-thumb:hover {
		background: rgba(var(--v-theme-on-surface), 0.18);
	}
}

.time-slot-chip {
	width: 100%;
	justify-content: center;
	transition: all 0.2s ease;
	margin: 0 !important;
	cursor: pointer;
	font-size: 0.8125rem;

	&:hover {
		transform: translateY(-1px);
		box-shadow: 0 2px 4px rgba(0, 0, 0, 0.07);
		background-color: rgba(var(--v-theme-on-surface), 0.04);
	}

	&.selected {
		font-weight: 500;
	}
}

.granularity-selector {
	width: 100%;
	overflow-x: auto;
	display: flex;
	justify-content: stretch;
	border: 1px solid rgba(var(--v-theme-on-surface), 0.12);
	border-radius: 8px;

	:deep(.v-btn) {
		letter-spacing: 0;
		font-size: 0.8125rem;
		height: 36px;
		border-right: 1px solid rgba(var(--v-theme-on-surface), 0.12);

		&:last-child {
			border-right: none;
		}

		&.v-btn--active {
			font-weight: 500;
		}
	}
}

.empty-state {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	min-height: 120px;
}

.selection-actions {
	.selection-btn {
		font-size: 0.75rem;
		letter-spacing: 0;
		min-width: auto;
	}
}

.gap-2 {
	gap: 8px;
}
</style>
